import { User, onAuthStateChanged, signOut } from 'firebase/auth'
import { set as firebaseSet } from 'firebase/database'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { create } from 'zustand'

import { auth } from 'features/firebase/auth'
import { playersRef } from 'features/firebase/database'
import { Player } from 'features/firebase/types'
import { setPlayerConnection, subscribeToUserInfo, upsertUserInfo } from 'features/firebase/user-operations'

interface AuthState {
  user: User | null
  player: Player | null
  loading: boolean
  setUser: (user: User | null) => void
  setPlayer: (player: Player) => void
  setLoading: (loading: boolean) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>(set => ({
  user: null,
  player: null,
  loading: true,
  setUser: user => set({ user }),
  setPlayer: (player: Player) => set({ player }),
  setLoading: loading => set({ loading }),
  logout: async () => {
    const { user } = useAuthStore.getState()
    if (user) {
      firebaseSet(playersRef(`${user.uid}/online`), false)
    }

    await signOut(auth)
    set({ user: null, player: null })
  },
}))

export const useAuthenticatedUser = () => {
  const navigate = useNavigate()
  const { user, player } = useAuthStore()

  useEffect(() => {
    if (!user || !player) {
      navigate('/login')
    }
  }, [user, player, navigate])

  if (!user || !player) {
    throw new Error('Redirecting to login')
  }

  return { user, player }
}

onAuthStateChanged(auth, async user => {
  const { setUser, setLoading, setPlayer } = useAuthStore.getState()

  if (!user) {
    setLoading(false)
    return
  }

  setUser(user)
  await upsertUserInfo(user)
  subscribeToUserInfo(user.uid, u => {
    setPlayer(u)
    setLoading(false)
  })

  setPlayerConnection(user.uid)
})
